import { z } from 'zod';

/**
 * Colonnes du panneau d'actions rapides de l'espace Assistant (Case Discovery).
 * Persistées dans `user_prompt_library.category` (migration 175). Une ligne
 * sans catégorie explicite (anciens clients, add-in Word) vaut `analyse`.
 */
export const QuickActionCategorySchema = z.enum(['redaction', 'dossier', 'analyse']);
export type QuickActionCategory = z.infer<typeof QuickActionCategorySchema>;
export const DEFAULT_QUICK_ACTION_CATEGORY: QuickActionCategory = 'analyse';

/**
 * Clés stables des actions natives. PERMANENTES : une clé stockée en base
 * (`user_prompt_library.native_key`) porte l'ordre et le masquage choisis par
 * l'avocat. Renommer une clé équivaut à supprimer la native puis à en créer
 * une autre — les préférences de l'avocat sont perdues. Les quatre clés
 * dossier reprennent les identifiants historiques du dock dossier
 * (`DossierQuickActionId`), dont dépendent les passages de relais depuis
 * RenumberPiecesDialog et renumberAssistantHandoff.
 */
export const NativeQuickActionKeySchema = z.enum([
  'redact-assignation',
  'redact-conclusions-replique',
  'redact-acte',
  'organize-pieces',
  'generate-bordereau-from-act',
  'renumber-pieces',
  'rename-pieces',
  'split-pdf',
  'compare-with-case',
  'reuse-argument-from-case',
  'client-cases-overview',
]);
export type NativeQuickActionKey = z.infer<typeof NativeQuickActionKeySchema>;

/** Choix proposés par une native à sélecteur (la carte « Rédiger un acte… »). */
export interface NativeQuickActionChoice {
  label: string;
  prompt: string;
  /**
   * Fragment, en minuscules, qui doit apparaître dans au moins un
   * `displayLabel` du catalogue d'actes (`ACT_CATALOG`) : proposer un type
   * d'acte absent romprait le pipeline dès `list_available_acts`.
   */
  actLabelFragment: string;
}

export interface NativeQuickAction {
  key: NativeQuickActionKey;
  category: Exclude<QuickActionCategory, 'analyse'>;
  title: string;
  description: string;
  prompt: string;
  /** Semée masquée : présente dans « Gérer », absente de la vue repliée. */
  initiallyHidden?: boolean;
  choices?: readonly NativeQuickActionChoice[];
}

/**
 * Prompt partagé par les natives de rédaction d'acte — fait passer l'agent
 * par le vrai pipeline (list_available_acts → orientation validée →
 * start_research_only_job → start_generation_only_job) plutôt que de rédiger
 * en roue libre. Vocabulaire aligné mot pour mot sur les consignes données à
 * l'agent dans ToolDefinitions.ts (description de start_research_only_job,
 * « faire arbitrer la stratégie par l'avocat ») et redactionMode.ts
 * (« workflow canonique ») — vérifié le 2026-09-10. Ne pas réinventer un
 * vocabulaire différent, qui désynchroniserait le prompt utilisateur des
 * instructions système.
 */
export function buildActeQuickActionPrompt(acteLabel: string): string {
  return `Je veux rédiger ${acteLabel} pour ce dossier. Identifie d'abord dans le catalogue l'acte qui correspond à la spécialisation et à l'étape de procédure de ce dossier. Lis les pièces et les écritures pour construire une orientation : prétentions envisagées, moyens de droit mobilisables et pièces qui les fondent. Présente-moi cette orientation et valide-la avec moi avant de lancer la génération du plan détaillé.`;
}

/**
 * Catalogue des actions natives, source unique pour :
 * - l'amorçage en base (`UserPromptLibraryRepository`) ;
 * - la surcharge du texte à la lecture — le texte stocké en base n'est qu'un
 *   instantané, faire évoluer un prompt ici suffit, sans migration ;
 * - le dock dossier (`dossierQuickActions.ts`), qui affiche ses actions
 *   fixes sans jamais lire les préférences de l'avocat.
 *
 * L'ordre du tableau fixe l'ordre initial dans chaque colonne. « Renommer mes
 * pièces » est semée masquée : décision produit du 2026-09-09 (3 actions par
 * colonne), réactivable par l'avocat via « Gérer ».
 */
export const NATIVE_QUICK_ACTIONS: readonly NativeQuickAction[] = [
  {
    key: 'redact-assignation',
    category: 'redaction',
    title: 'Rédiger une assignation',
    description: 'Identifie le bon acte, propose une orientation, fait valider avant de lancer',
    prompt: buildActeQuickActionPrompt('une assignation'),
  },
  {
    key: 'redact-conclusions-replique',
    category: 'redaction',
    title: 'Rédiger des conclusions en réplique',
    description: 'Analyse les écritures adverses, propose une orientation, fait valider avant de lancer',
    prompt: buildActeQuickActionPrompt('des conclusions en réplique'),
  },
  {
    key: 'redact-acte',
    category: 'redaction',
    title: 'Rédiger un acte…',
    description: "Choisir le type d'acte à générer",
    // Utilisé seulement hors de l'interface (lecture par l'agent) : la carte
    // ouvre un sélecteur et envoie le prompt du type choisi.
    prompt: buildActeQuickActionPrompt('un acte'),
    choices: [
      { label: 'Mise en demeure', prompt: buildActeQuickActionPrompt('une mise en demeure'), actLabelFragment: 'mise en demeure' },
      { label: 'Conclusions en défense', prompt: buildActeQuickActionPrompt('des conclusions en défense'), actLabelFragment: 'conclusions en défense' },
      { label: 'Mémoire', prompt: buildActeQuickActionPrompt('un mémoire'), actLabelFragment: 'mémoire' },
      { label: 'Requête', prompt: buildActeQuickActionPrompt('une requête'), actLabelFragment: 'requête' },
    ],
  },
  {
    key: 'organize-pieces',
    category: 'dossier',
    title: 'Organiser mes pièces',
    description: 'Proposer dossiers, déplacements et regroupements à confirmer.',
    prompt: 'Analyse les pièces de ce dossier et propose une organisation plus claire : dossiers à créer, pièces à déplacer, regroupements utiles et éventuelles incohérences de classement. Présente-moi d’abord une proposition structurée à confirmer. Ne crée, ne déplace et ne renomme aucune pièce avant ma confirmation explicite.',
  },
  {
    key: 'generate-bordereau-from-act',
    category: 'dossier',
    title: 'Générer un bordereau à partir d’un acte',
    description: 'Numéroter depuis un acte, puis ouvrir le bordereau.',
    prompt: [
      'Je veux générer un bordereau à partir d’un acte.',
      '',
      'Procède ainsi :',
      '1. Si un acte est joint à ce message, lis-le. Sinon, consulte Mes écritures dans /procedure, présente-moi le ou les actes disponibles et ATTENDS toujours mon choix explicite, même s’il n’y en a qu’un.',
      '2. Dans l’acte retenu, relève le bordereau ou la liste des pièces et leurs numéros.',
      '3. Récupère l’inventaire complet de Nos pièces, y compris celles qui portent déjà un numéro. Limite le rapprochement à cette catégorie.',
      '4. Présente-moi le rapprochement proposé (pièce du dossier → numéro), ainsi que les pièces absentes ou ambiguës, puis ATTENDS ma validation explicite.',
      '5. Après ma validation seulement, applique la numérotation avec l’outil prévu.',
      '6. Après l’application réussie seulement, affiche un bouton court contextualisé « Bordereau — [nom de l’acte] » (40 caractères maximum) pour ouvrir dans le générateur exactement les pièces rapprochées, éligibles et numérotées.',
      '',
      'N’invente aucune correspondance et ne modifie aucun numéro avant mon accord.',
    ].join('\n'),
  },
  {
    key: 'renumber-pieces',
    category: 'dossier',
    title: 'Renuméroter mes pièces',
    description: 'Choisir une référence puis proposer une numérotation.',
    prompt: 'Aide-moi à renuméroter les pièces de ce dossier. Commence par me demander quelle référence utiliser : ordre chronologique, bordereau existant, acte/écriture, ou consignes spécifiques. Ensuite, propose un plan de renumérotation à confirmer. Ne modifie aucune numérotation avant ma confirmation explicite.',
  },
  {
    key: 'rename-pieces',
    category: 'dossier',
    title: 'Renommer mes pièces',
    description: 'Proposer des noms plus clairs et homogènes à confirmer.',
    prompt: 'Analyse les noms des pièces de ce dossier et propose des renommages plus clairs, homogènes et exploitables dans un bordereau. Garde les informations utiles comme la date, la nature du document, l’auteur ou la partie concernée quand elles sont identifiables. Présente-moi d’abord la liste des renommages proposés à confirmer. Ne renomme aucune pièce avant ma confirmation explicite.',
  },
  {
    key: 'split-pdf',
    category: 'dossier',
    title: 'Découper une pièce ou un fichier',
    description: 'Scinder un PDF qui regroupe plusieurs pièces, après accord sur le tableau.',
    prompt: 'Je veux découper un PDF de ce dossier qui regroupe plusieurs pièces. Si je ne t’ai pas indiqué lequel, liste les PDF du dossier qui semblent en regrouper plusieurs et demande-moi lequel découper. Lis ensuite tout son texte, page par page, puis présente-moi un tableau de correspondance (numéro, titre, pages) dans l’ordre des pages, en signalant tes doutes. Ne lance aucune découpe avant ma réponse explicite dans le chat.',
  },
  // Les trois natives multi-dossiers font choisir le dossier avant
  // `add_related_case` : `search_cases` peut rendre des homonymes, et ajouter
  // un dossier ouvre la conversation à ses pièces.
  {
    key: 'compare-with-case',
    category: 'dossier',
    title: 'Comparer avec un autre dossier',
    description: 'Mettre ce dossier en regard d’un dossier voisin du cabinet.',
    prompt: 'Je veux comparer ce dossier avec un autre dossier du cabinet. Si je ne t’ai pas dit lequel, ni sur quel point porter la comparaison, demande-le-moi. Cherche le dossier, présente-moi les dossiers trouvés avec leur client, et attends mon choix avant de l’ajouter à la conversation. Compare ensuite les deux dossiers sur ce point en citant les pièces de chacun : ce qui les rapproche, ce qui les distingue, et ce qu’on peut en tirer pour ce dossier.',
  },
  {
    key: 'reuse-argument-from-case',
    category: 'dossier',
    title: 'Reprendre une argumentation d’un autre dossier',
    description: 'Adapter à ce dossier un raisonnement déjà construit ailleurs.',
    prompt: 'Je veux reprendre pour ce dossier une argumentation déjà développée dans un autre dossier du cabinet. Si je ne t’ai pas dit quel dossier, ni sur quel point, demande-le-moi. Cherche le dossier, présente-moi les dossiers trouvés avec leur client, et attends mon choix avant de l’ajouter à la conversation. Retrouve l’argumentation dans ses écritures, puis adapte-la aux faits et aux pièces de ce dossier, en signalant ce qui ne se transpose pas.',
  },
  {
    key: 'client-cases-overview',
    category: 'dossier',
    title: 'Faire le point sur les dossiers du client',
    description: 'Synthèse des dossiers du cabinet pour ce même client.',
    prompt: 'Je veux faire le point sur tous les dossiers du cabinet pour le client de ce dossier. Cherche les dossiers de ce client, présente-moi la liste et attends que je choisisse ceux à ajouter à la conversation. Fais ensuite, pour chacun, un point court : objet du litige, étape de la procédure et derniers éléments au dossier. Termine par les liens entre ces dossiers.',
  },
];

const NATIVE_QUICK_ACTIONS_BY_KEY: ReadonlyMap<string, NativeQuickAction> = new Map(
  NATIVE_QUICK_ACTIONS.map((action) => [action.key, action]),
);

/** Garde de type : la valeur est-elle une clé native connue du catalogue ? */
export function isNativeQuickActionKey(value: unknown): value is NativeQuickActionKey {
  return typeof value === 'string' && NATIVE_QUICK_ACTIONS_BY_KEY.has(value);
}

/**
 * Entrée du catalogue pour une clé, ou `undefined` si la clé n'y est plus
 * (native retirée : la ligne correspondante en base est orpheline).
 */
export function findNativeQuickAction(key: string | null | undefined): NativeQuickAction | undefined {
  return key ? NATIVE_QUICK_ACTIONS_BY_KEY.get(key) : undefined;
}
